import type {
  ReplyEmailItem,
  ReplyResponseStatus,
} from "hooks/use-email-replies-api";

/**
 * 1 スレッド (= 1 起点となった outbound email) の集約結果.
 * 左ペインで「1 スレッド = 1 行」 の表示単位を作るために使う.
 */
export type ReplyThreadGroup = {
  /** グループ識別キー. provider_thread_id があればそれ、 無ければ起点 ID にフォールバック */
  key: string;
  /** スレッド内の最新リプライ (= ヘッダ行に表示する代表) */
  latest: ReplyEmailItem;
  /** スレッド内の全リプライ (受信時刻降順) */
  replies: ReplyEmailItem[];
  /** スレッド内の総リプライ数 */
  count: number;
  /** スレッド全体のステータス. unresponded > in_progress > completed の優先度集約 */
  aggregateStatus: ReplyResponseStatus;
  /**
   * スレッドの起点ラベル (UI 表示用):
   *   配信シリーズに属する一斉配信 → "シリーズ「アナスタシアさん紹介」"
   *   シリーズ未所属の一斉配信     → "配信「春のキャンペーン」"
   *   個別送信 (1:1 提案)          → "提案「Power BI 案件…」"
   *   いずれも紐付け無し           → null
   */
  originLabel: string | null;
};

const STATUS_PRIORITY: Record<ReplyResponseStatus, number> = {
  unresponded: 0, // 最優先 (未対応が 1 件でもあれば全体未対応)
  in_progress: 1,
  completed: 2,
};

/**
 * リプライ群をスレッド単位 (provider_thread_id) でグルーピングする.
 *
 * グルーピング規則:
 *   - provider_thread_id が同じリプライは同一スレッド (= Gmail のスレッド ID)
 *   - provider_thread_id が null のリプライは fallback として reply.id 単独でグループ化
 *     (実質 1 件 1 グループ。 ISSUE-783 でほぼ存在しないはずだが防御的に)
 *
 * 並び順:
 *   - 各グループ内: receivedAt 降順 (latest が先頭)
 *   - グループ自体: 最新リプライの receivedAt 降順
 *
 * @param {ReplyEmailItem[]} replies フラットなリプライ一覧
 * @return {ReplyThreadGroup[]} スレッド単位のグループ
 */
export function groupRepliesByThread(
  replies: ReplyEmailItem[],
): ReplyThreadGroup[] {
  const buckets = new Map<string, ReplyEmailItem[]>();
  for (const r of replies) {
    const key = r.providerThreadId ?? `__no_thread__:${r.id}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(r);
    else buckets.set(key, [r]);
  }
  const groups: ReplyThreadGroup[] = [];
  for (const [key, group] of buckets) {
    group.sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
    const latest = group[0];
    groups.push({
      key,
      latest,
      replies: group,
      count: group.length,
      aggregateStatus: aggregateStatus(group),
      originLabel: deriveOriginLabel(latest),
    });
  }
  groups.sort((a, b) => b.latest.receivedAt.localeCompare(a.latest.receivedAt));
  return groups;
}

/**
 * スレッド全体のステータスを優先度集約で決定する.
 * 1 件でも unresponded があればスレッド全体を unresponded 扱い.
 * @param {ReplyEmailItem[]} replies スレッド内のリプライ
 * @return {ReplyResponseStatus} 集約ステータス
 */
function aggregateStatus(replies: ReplyEmailItem[]): ReplyResponseStatus {
  let best: ReplyResponseStatus = "completed";
  for (const r of replies) {
    if (STATUS_PRIORITY[r.responseStatus] < STATUS_PRIORITY[best]) {
      best = r.responseStatus;
    }
  }
  return best;
}

/**
 * リプライから起点ラベルを推定する.
 * 「これはどの配信 / シリーズ / 個別送信から派生したスレッドか」 を 1 行で示す.
 * @param {ReplyEmailItem} reply 代表リプライ
 * @return {string | null} 起点ラベル (該当なしは null)
 */
function deriveOriginLabel(reply: ReplyEmailItem): string | null {
  if (reply.parentType === "delivery") {
    if (reply.seriesName) return `シリーズ「${reply.seriesName}」`;
    if (reply.deliverySubject) return `配信「${reply.deliverySubject}」`;
    return "一斉配信";
  }
  if (reply.parentType === "sent_email") {
    if (reply.sentEmailSubject) return `提案「${reply.sentEmailSubject}」`;
    return "個別送信";
  }
  return null;
}
